import { useState, useEffect } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { useNavigate, useLocation } from "react-router-dom";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";

import { BulkImportStepsContainer, BulkImportStep } from "./stepsContainer";

const STEP_PARAM = "step";
const ALLOWED_URL_STEPS: BulkImportStep[] = ["export", "upload"];

function parseBulkAssignStepFromUrlParam(value: string | null): BulkImportStep {
    switch (value) {
        case "export":
        case "upload":
        case "confirm":
            return value;
        default:
            return "export";
    }
}

export function ControlsBulkImportPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState<BulkImportStep>("export");
    const [hasError, setHasError] = useState(false);

    const getStepStatus = (stepId: BulkImportStep): "error" | undefined => {
        return currentStep === stepId && hasError ? "error" : undefined;
    };

    const handleStepChange = (step: BulkImportStep) => {
        setCurrentStep(step);

        if (ALLOWED_URL_STEPS.includes(step)) {
            const params = new URLSearchParams(location.search);
            params.set(STEP_PARAM, step);
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stepParam = params.get(STEP_PARAM);

        if (
            stepParam &&
            ALLOWED_URL_STEPS.includes(
                parseBulkAssignStepFromUrlParam(stepParam)
            )
        ) {
            const parsedStep = parseBulkAssignStepFromUrlParam(stepParam);
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setCurrentStep(parsedStep);
        }
    }, [location.search]);

    return (
        <OrgApplicationBar
            title={{
                title: t("ControlsBulkImport_PageTitle"),
                breadCrumbs: [
                    {
                        label: t("ControlManagement_ModuleTitle"),
                        url: "/controls",
                    },
                ],
            }}
            navigation={{
                type: "Steps",
                currentStepId: currentStep,
                stepSpecs: [
                    {
                        id: "export",
                        label: t("ControlsBulkImport_Step1_Title"),
                        status: getStepStatus("export"),
                        onClick: () => handleStepChange("export"),
                    },
                    {
                        id: "upload",
                        label: t("ControlsBulkImport_Step2_Title"),
                        status: getStepStatus("upload"),
                        onClick: () => handleStepChange("upload"),
                    },
                    {
                        id: "confirm",
                        label: t("ControlsBulkImport_Step3_Title"),
                        status: getStepStatus("confirm"),
                        onClick: () => {},
                    },
                ],
            }}
        >
            <BulkImportStepsContainer
                currentStep={currentStep}
                onStepChange={handleStepChange}
                onErrorChange={setHasError}
            />
        </OrgApplicationBar>
    );
}
